import { LoadingButton } from "@mui/lab";
import {
  Box,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { useContext, useEffect, useState } from "react";
import { OrganizationNiche } from "schemas/dashboard";
import { LIGHT_GREY } from "styles/oneOffColor";

import MergeTagChip from "components/Editor/MergeTagWarning/MergeTagChip";
import { Template } from "features/Influencer/ContactList/schema";
import { containsMergeTagsTemplates } from "utils/string";
import { trackEvent } from "utils/tracking";

enum Screen {
  merge_tag = "merge_tag",
  shared_niches = "niche",
  pending_emails = "pending_emails",
  uncheck_niches = "uncheck_niches",
}

interface Props {
  loading: boolean;
  open: boolean;
  handleClose: () => void;
  template: Template;
  handleConfirm: (
    updatePendingEmails?: boolean,
    proceedWithUncheckedNiches?: boolean,
  ) => void;
  createNewForNiche: () => void;
  listOfUncheckNiches: OrganizationNiche[];
}

const PreSaveDialog = ({
  open,
  loading,
  handleClose,
  template,
  handleConfirm,
  createNewForNiche,
  listOfUncheckNiches,
}: Props) => {
  const hasMergeTag = containsMergeTagsTemplates(template?.body || "");
  const { isEdited } = useContext(QuickSendDrawerContext);
  const { associatedNicheId } = useContext(OutreachTemplatesContext);

  const [screen, setScreen] = useState<Screen | undefined>(undefined);

  const getDialogTitle = () => {
    if (screen === Screen.shared_niches) {
      return "You're editing a shared template";
    } else if (screen === Screen.pending_emails) {
      return template?.attachedScheduledEmailsCount
        ? `You already scheduled emails with this template`
        : "Update Current Draft?";
    } else if (screen === Screen.merge_tag) {
      return "No merge tags detected";
    } else {
      return "";
    }
  };

  const getDialogContent = () => {
    if (screen === Screen.uncheck_niches && listOfUncheckNiches?.length > 0) {
      return (
        <>
          This template will no longer be use for brand in these categories:{" "}
          <strong>
            {listOfUncheckNiches.map((niche) => niche.name).join(", ")}
          </strong>
          .
          <br />
          We will pause outreach on these niches until you pick a new template
          on your Virtual Assistant dashboard. Do you want to continue?
        </>
      );
    } else if (screen === Screen.shared_niches) {
      return `This template is shared across ${template?.attachedNichesCount} niches. Want to update it everywhere, or save a new version just for this niche?`;
    } else if (screen === Screen.pending_emails) {
      return (
        <>
          {template?.attachedScheduledEmailsCount
            ? `You have ${template?.attachedScheduledEmailsCount} scheduled emails using this template`
            : "You are editing a draft using this template."}{" "}
          Do you want to update them with the latest changes?
        </>
      );
    } else if (screen === Screen.merge_tag) {
      return (
        <>
          Using merge tags like <MergeTagChip mergeTag="{First Name}" /> and{" "}
          <MergeTagChip mergeTag="{Brand Name}" /> helps personalizing your
          helps personalize your outreach. Are you sure you want to continue
          without them?`
        </>
      );
    } else {
      return "";
    }
  };

  const updateTemplateForAllNiches = () => {
    trackEvent("Applies To All Use of Template Button Clicked", {
      "Template ID": template.id,
    });
    if (
      screen === Screen.shared_niches &&
      template?.attachedScheduledEmailsCount
    ) {
      setScreen(Screen.pending_emails);
    } else {
      handleConfirm(false);
    }
  };

  useEffect(() => {
    if (listOfUncheckNiches.length > 0) {
      setScreen(Screen.uncheck_niches);
    } else if (!hasMergeTag) {
      setScreen(Screen.merge_tag);
    } else if (template?.attachedNichesCount && associatedNicheId) {
      setScreen(Screen.shared_niches);
    } else if (template?.attachedScheduledEmailsCount || isEdited) {
      setScreen(Screen.pending_emails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, hasMergeTag, isEdited, associatedNicheId]);

  if (!screen) return <></>;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Grid container justifyContent={"flex-end"}>
        <IconButton sx={{ color: grey[300] }}>
          <Box
            component={"i"}
            className="fa-regular fa-xmark"
            onClick={handleClose}
          />
        </IconButton>
      </Grid>
      <Grid container justifyContent={"center"} sx={{ mt: 2 }}>
        <Box
          sx={{
            width: "max-content",
            backgroundColor: "#f0f4f3",
            px: 2,
            py: 1,
            borderRadius: 6,
          }}
        >
          <Typography sx={{ color: "primary.main" }}>
            ⚠️ Before you save...
          </Typography>
        </Box>
      </Grid>

      <DialogTitle>
        <Typography variant="h4" textAlign={"center"} gutterBottom>
          {getDialogTitle()}
        </Typography>

        <Typography
          gutterBottom
          textAlign={"center"}
          sx={{ px: { xs: 0, md: 2 } }}
        >
          {getDialogContent()}
        </Typography>
      </DialogTitle>

      <DialogActions>
        <Grid container gap={2} alignItems="center" sx={{ px: 3, pb: 3 }}>
          <Grid
            container
            flexDirection={"column"}
            alignItems="center"
            gap={1}
            item
            xs={6}
          >
            {screen === Screen.uncheck_niches && (
              <LoadingButton
                onClick={handleClose}
                loading={loading}
                fullWidth
                sx={{ backgroundColor: LIGHT_GREY, px: 2, py: 1.5 }}
              >
                Cancel
              </LoadingButton>
            )}
            {screen === Screen.shared_niches && (
              <>
                <LoadingButton
                  loading={loading}
                  fullWidth
                  sx={{ backgroundColor: LIGHT_GREY, px: 2, py: 1.5 }}
                  onClick={updateTemplateForAllNiches}
                >
                  Update this template
                </LoadingButton>

                <Typography
                  textAlign="center"
                  variant="caption"
                  color="textSecondary"
                >
                  Applies to all use of this template
                </Typography>
              </>
            )}

            {screen === Screen.pending_emails && (
              <LoadingButton
                onClick={() => handleConfirm(true)}
                loading={loading}
                fullWidth
                sx={{ backgroundColor: LIGHT_GREY, px: 2, py: 1.5 }}
              >
                {isEdited && !template?.attachedScheduledEmailsCount
                  ? "Update my draft"
                  : "Update all pending emails"}
              </LoadingButton>
            )}
            {screen === Screen.merge_tag && (
              <LoadingButton
                fullWidth
                sx={{ backgroundColor: LIGHT_GREY, px: 2, py: 1.5 }}
                onClick={() => {
                  if (template?.attachedNichesCount && associatedNicheId) {
                    setScreen(Screen.shared_niches);
                  } else if (template?.attachedScheduledEmailsCount) {
                    setScreen(Screen.pending_emails);
                  } else {
                    handleConfirm(false);
                  }
                }}
                loading={loading}
              >
                Continue
              </LoadingButton>
            )}
          </Grid>

          <Grid
            item
            container
            flexDirection={"column"}
            alignItems="center"
            gap={1}
            xs
          >
            {screen === Screen.shared_niches && (
              <>
                <LoadingButton
                  loading={loading}
                  onClick={createNewForNiche}
                  fullWidth
                  variant="contained"
                  sx={{ px: 2, py: 1.5 }}
                >
                  Save as a new template
                </LoadingButton>
                <Typography
                  textAlign="center"
                  variant="caption"
                  color="textSecondary"
                >
                  Create a copy for just this niche
                </Typography>
              </>
            )}
            {screen === Screen.pending_emails && (
              <LoadingButton
                onClick={() => handleConfirm(false)}
                loading={loading}
                fullWidth
                sx={{ px: 2, py: 1.5 }}
                variant="contained"
              >
                Leave as is
              </LoadingButton>
            )}

            {screen === Screen.uncheck_niches && (
              <LoadingButton
                onClick={() => handleConfirm(false, true)}
                loading={loading}
                fullWidth
                sx={{ px: 2, py: 1.5 }}
                variant="contained"
              >
                Confirm
              </LoadingButton>
            )}
            {screen === Screen.merge_tag && (
              <LoadingButton
                onClick={() => handleClose()}
                loading={loading}
                fullWidth
                sx={{ px: 2, py: 1.5 }}
                variant="contained"
              >
                Keep Editing
              </LoadingButton>
            )}
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default PreSaveDialog;
